import { inject } from 'vue';
import { useStore } from 'vuex';

import { PROVIDE_KEYS } from '@/shared/assets/constants';

/**
 * Normalizes field path to schema_property format (e.g. custom.field -> custom:field).
 */
function fieldToSchemaProperty(field) {
  if (!field) return null;
  if (field.includes(':')) return field;
  return field.replace(/\./g, ':');
}

/**
 * Returns alternative schema_property formats for help document matching.
 * Help docs store highlights as "highlights:curriculum" but form fields use
 * "highlights.properties.curriculum". This ensures both match.
 */
function schemaPropertyVariants(field) {
  if (!field) return [];
  const withDots = field;
  const withColons = fieldToSchemaProperty(field);
  const variants = [withDots, withColons];
  // highlights.properties.X <=> highlights:X (propertyOptions uses shorthand)
  const highlightsPropsMatch = field.match(/^highlights\.properties\.(.+)$/);
  if (highlightsPropsMatch) {
    variants.push(`highlights:${highlightsPropsMatch[1]}`);
  }
  return [...new Set(variants)];
}

/**
 * Works with form input fields to leverage our schematized field definitions.
 * (see LabeledSelect.vue for example of use)
 *
 * Append emits to your component emit definition
 * Append props to your component prop definition
 *
 * @param {*} fieldRef
 * @param {*} hardLockRef
 * @param {*} mandatoryRef
 * @param {*} messageRef
 * @param {*} schemaIdRef
 * @returns {
 *  enabled: boolean
 *  helpDocument: ref
 *  label: ref
 *  ariaLabel: string
 *  locked: ref
 *  schemaMask: any
 *  propertyBySchema: ref
 *  getLabelByProperty: function
 *  openHelpFlyout: function
 * }
 */
export default function useSchematizedField(
  fieldRef,
  hardLockRef,
  mandatoryRef,
  messageRef,
  schemaIdRef,
) {
  const $bct = inject('translator');
  const store = useStore();
  const openHelpFlyout = inject(PROVIDE_KEYS.OPEN_HELP_FLYOUT, null);

  const schema = computed(() => {
    return store.state.schemas[schemaIdRef.value];
  });

  const helpDocument = computed(() => {
    if (!schemaIdRef.value || !fieldRef.value) return null;
    const docs = window.helpDocuments || [];
    if (!Array.isArray(docs) || docs.length === 0) return null;
    const variants = schemaPropertyVariants(fieldRef.value);
    return (
      docs.find(
        (doc) =>
          doc.schema_id === schemaIdRef.value &&
          variants.some((variant) => doc.schema_property === variant),
      ) || null
    );
  });

  const schemaProperties = computed(() => {
    if (!fieldRef.value) return null;
    if (!schemaIdRef.value) throw new Error('schemaId required to use field');
    if (!schema.value) {
      throw new Error(`Schema missing from site schemas - ID: ${schemaIdRef.value}`);
    }

    let namespace;
    if (Object.keys(customProperties.value).includes(fieldRef.value)) {
      namespace = fieldRef.value
        .split('.')
        .reduce((value, index) => value[index], customProperties.value || {});
    } else {
      namespace = fieldRef.value
        .split('.')
        .reduce((value, index) => value[index], properties.value);
    }
    return namespace || {};
  });

  const enabled = computed(() => {
    if (!fieldRef.value) return true;
    return schemaProperties.value?.enabled;
  });

  const label = computed(() => {
    if (messageRef.value) return $bct(messageRef.value);
    return $bct(schemaProperties.value?.alias || schemaProperties.value?.title || '');
  });

  const ariaLabel = computed(() => {
    if (!label.value) return null;

    const translatedLabel = label.value;
    if (mandatoryRef.value) {
      const translatedRequired = $bct('required');
      return `${translatedLabel} - ${translatedRequired}`;
    }

    return translatedLabel;
  });

  const locked = computed(() => {
    if (hardLockRef.value) return true;
    if (schemaProperties.value) {
      return !(schemaProperties.value.editRoles || []).includes(store.state.role);
    }
    return false;
  });

  const schemaMask = computed(() => {
    return schemaProperties.value?.mask;
  });

  const properties = computed(() => {
    return schema.value?.definition?.properties;
  });

  const customProperties = computed(() => {
    return properties.value?.custom?.properties || {};
  });

  const propertyBySchema = computed(() => {
    if (fieldRef.value) {
      return (
        fieldRef.value.split('.').reduce((value, index) => value[index], properties.value) || {}
      );
    }
    return null;
  });

  function getLabelByProperty(property, defaultLabel) {
    return properties.value?.[property]?.alias || defaultLabel;
  }

  return {
    enabled,
    helpDocument,
    label,
    ariaLabel,
    locked,
    openHelpFlyout,
    schemaMask,
    propertyBySchema,
    getLabelByProperty,
  };
}

useSchematizedField.props = {
  field: {
    type: String,
    default: null,
  },
  hardLock: {
    type: Boolean,
    default: false,
  },
  hardLockMessage: {
    type: String,
    default: 'Locked',
  },
  mandatory: {
    type: Boolean,
    default: false,
  },
  message: {
    type: String,
    default: null,
  },
  schemaId: {
    type: String,
    default: null,
  },
  enforceMinMaxRules: {
    type: Boolean,
    default: true,
  },
};

// Must match order of params accepted by composable
useSchematizedField.paramKeys = ['field', 'hardLock', 'mandatory', 'message', 'schemaId'];
